import { useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthProvider.jsx'
import { createUser, listUsers, updateUser, disableUser, enableUser, deleteUser, USER_TYPES } from '../auth/auth.js'
import { addAuditLog } from '../data/audit.js'
import Toast from '../components/Toast.jsx'
import Modal from '../components/Modal.jsx'
import { canManageUsers, canAccessSettings, canMoveStock, canEditCatalog, canViewCatalog, canViewReports, canAccessCompras, canConfirmCompras, canViewHistoricoCompras, canCreatePedidos } from '../utils/permissions.js'

// Converter USER_TYPES (objeto) para array de opções do select
const PERFIS_OPTIONS = [
  { value: USER_TYPES.admin, label: 'Administrador' },
  { value: USER_TYPES.almoxarife, label: 'Almoxarife' },
  { value: USER_TYPES.comprador, label: 'Comprador' }
]

function getPermissionsDescription(perfil) {
  const permissions = []
  if (canManageUsers(perfil)) permissions.push('Gerenciar usuários')
  if (canAccessSettings(perfil)) permissions.push('Acessar configurações')
  if (canMoveStock(perfil)) permissions.push('Mover estoque')
  if (canEditCatalog(perfil)) permissions.push('Editar catálogo')
  if (canViewCatalog(perfil)) permissions.push('Ver catálogo')
  if (canViewReports(perfil)) permissions.push('Ver relatórios')
  if (canCreatePedidos(perfil)) permissions.push('Criar pedidos de compra')
  if (canAccessCompras(perfil)) permissions.push('Acessar compras')
  if (canConfirmCompras(perfil)) permissions.push('Confirmar compras')
  if (canViewHistoricoCompras(perfil)) permissions.push('Ver histórico de compras')
  return permissions.length > 0 ? permissions.join(', ') : 'Sem permissões'
}

export default function UsersPage() {
  const { session } = useAuth()
  const [users, setUsers] = useState([])
  const [toast, setToast] = useState(null)
  const [toastType, setToastType] = useState('info')
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [showPin, setShowPin] = useState(false)

  const [form, setForm] = useState({
    nome: '',
    login: '',
    senha: '',
    perfil: 'almoxarife'
  })

  const [editingUser, setEditingUser] = useState(null)
  const [deleteModal, setDeleteModal] = useState({ open: false, user: null })

  async function reload() {
    try {
      const all = await listUsers()
      setUsers(all.sort((a, b) => (a.nome || '').localeCompare(b.nome || '')))
    } catch (err) {
      setToastType('error')
      setToast('Erro ao carregar usuários: ' + err.message)
    }
  }

  useEffect(() => {
    reload()
  }, [session?.empresaId])

  function openEdit(user) {
    setEditingUser(user)
    setForm({
      nome: user.nome || '',
      login: user.login || '',
      senha: '',
      perfil: user.perfil || 'almoxarife'
    })
    setOpen(true)
  }

  function openCreate() {
    setEditingUser(null)
    setForm({
      nome: '',
      login: '',
      pin: '',
      perfil: 'almoxarife'
    })
    setOpen(true)
  }

  async function onCreate(e) {
    e.preventDefault()
    
    const errors = []
    
    if (!form.nome.trim()) {
      errors.push('Nome é obrigatório')
    } else if (form.nome.trim().length < 3) {
      errors.push('Nome deve ter pelo menos 3 caracteres')
    }
    
    if (!form.login.trim()) {
      errors.push('Login é obrigatório')
    } else if (form.login.trim().length < 3) {
      errors.push('Login deve ter pelo menos 3 caracteres')
    } else if (!/^[a-zA-Z0-9_@.-]+$/.test(form.login.trim())) {
      errors.push('Login deve conter apenas letras, números, underscore, @ e ponto')
    }
    
    if (!form.senha || !form.senha.trim()) {
      errors.push('Senha é obrigatória')
    } else if (!/^\d{4,6}$/.test(form.senha.trim())) {
      errors.push('Senha deve ter 4 a 6 dígitos numéricos')
    }
    
    if (errors.length > 0) {
      setToastType('error')
      setToast(errors.join('. '))
      return
    }

    setBusy(true)
    try {
      const userId = await createUser({
        nome: form.nome.trim(),
        login: form.login.trim().toLowerCase(),
        senha: form.senha.trim(),
        perfil: form.perfil
      })
      
      console.log('[UsersPage] TESTE - Usuário criado:', {
        nome: form.nome.trim(),
        login: form.login.trim().toLowerCase(),
        senha: form.senha.trim(),
        perfil: form.perfil,
        userId: userId
      })

      await addAuditLog({
        usuarioId: session?.usuarioId ?? null,
        usuarioNome: session?.usuarioNome ?? null,
        acao: 'USUARIO_CRIADO',
        detalhes: JSON.stringify({ login: form.login, perfil: form.perfil }),
        tabelaAfetada: 'users',
        registroId: userId
      })

      setToastType('success')
      setToast(`Usuário criado! Login: ${form.login} | Senha: ${form.senha}`)
      setOpen(false)
      setForm({ nome: '', login: '', senha: '', perfil: 'almoxarife' })
      await reload()
    } catch (err) {
      setToastType('error')
      setToast(err?.message || 'Falha ao criar usuário.')
    } finally {
      setBusy(false)
    }
  }

  async function onUpdate(e) {
    e.preventDefault()
    
    const errors = []
    
    if (!form.nome.trim()) {
      errors.push('Nome é obrigatório')
    } else if (form.nome.trim().length < 3) {
      errors.push('Nome deve ter pelo menos 3 caracteres')
    }
    
    if (form.senha.trim() && !/^\d{4,6}$/.test(form.senha.trim())) {
      errors.push('Senha deve ter 4 a 6 dígitos numéricos')
    }
    
    if (errors.length > 0) {
      setToastType('error')
      setToast(errors.join('. '))
      return
    }

    setBusy(true)
    try {
      const updates = {
        nome: form.nome.trim(),
        perfil: form.perfil
      }
      if (form.senha && form.senha.trim()) updates.senha = form.senha.trim()
      await updateUser(editingUser.id, updates)

      await addAuditLog({
        usuarioId: session?.usuarioId ?? null,
        usuarioNome: session?.usuarioNome ?? null,
        acao: 'USUARIO_ATUALIZADO',
        detalhes: JSON.stringify({ login: editingUser.login }),
        tabelaAfetada: 'users',
        registroId: editingUser.id
      })

      setToastType('success')
      setToast('Usuário atualizado.')
      setOpen(false)
      setEditingUser(null)
      setForm({ nome: '', login: '', senha: '', perfil: 'almoxarife' })
      await reload()
    } catch (err) {
      setToastType('error')
      setToast(err?.message || 'Falha ao atualizar.')
    } finally {
      setBusy(false)
    }
  }

  async function confirmDelete() {
    if (!deleteModal.user) return

    try {
      await deleteUser(deleteModal.user.id)

      await addAuditLog({
        usuarioId: session?.usuarioId ?? null,
        usuarioNome: session?.usuarioNome ?? null,
        acao: 'USUARIO_EXCLUIDO',
        detalhes: JSON.stringify({ login: deleteModal.user.login, nome: deleteModal.user.nome }),
        tabelaAfetada: 'users',
        registroId: deleteModal.user.id
      })

      setToastType('success')
      setToast('Usuário excluído permanentemente.')
      setDeleteModal({ open: false, user: null })
      await reload()
    } catch (err) {
      setToastType('error')
      setToast(err?.message || 'Falha ao desativar.')
    }
  }

  async function toggleAtivo(user) {
    try {
      if (user.ativo) {
        await disableUser(user.id)
      } else {
        await enableUser(user.id)
      }
      
      await addAuditLog({
        usuarioId: session?.usuarioId ?? null,
        usuarioNome: session?.usuarioNome ?? null,
        acao: user.ativo ? 'USUARIO_DESATIVADO' : 'USUARIO_ATIVADO',
        detalhes: JSON.stringify({ login: user.login }),
        tabelaAfetada: 'users',
        registroId: user.id
      })
      
      setToastType('success')
      setToast(user.ativo ? 'Usuário desativado.' : 'Usuário ativado.')
      await reload()
    } catch (err) {
      setToastType('error')
      setToast(err?.message || 'Falha ao alterar status.')
    }
  }

  return (
    <div className="space-y-4">
      <Toast message={toast} type={toastType} onClose={() => setToast(null)} />

      <div className="rounded-2xl cm-card p-4">
        <div className="text-sm font-semibold">Gerenciar Usuários</div>
        <div className="mt-1 text-xs text-white/70">Criar, ativar/desativar usuários.</div>
      </div>

      <button
        onClick={openCreate}
        className="w-full rounded-xl bg-accent-600 px-3 py-3 text-sm font-semibold text-white"
      >
        + Novo usuário
      </button>

      <div className="space-y-2">
        {users.map((u) => (
          <div key={u.id} className="rounded-2xl cm-card p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold">{u.nome}</div>
                <div className="mt-1 text-xs text-white/70">
                  <span className="font-mono bg-white/10 px-1 rounded">{u.login}</span> • {PERFIS_OPTIONS.find(p => p.value === u.perfil)?.label || u.perfil}
                </div>
                <div className="mt-1 text-xs text-white/50">
                  Email: {u.login}
                </div>
                <div className={`mt-1 text-xs ${u.ativo ? 'text-emerald-400' : 'text-red-400'}`}>
                  {u.ativo ? '✓ Ativo' : '✗ Inativo'}
                </div>
                <div className="mt-1 text-xs text-white/50">
                  Permissões: {getPermissionsDescription(u.perfil)}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                {u.login !== 'admin' && (
                  <button
                    onClick={() => toggleAtivo(u)}
                    className={`rounded-lg px-3 py-1.5 text-[10px] font-semibold ${u.ativo ? 'bg-red-600/80' : 'bg-emerald-600/80'}`}
                  >
                    {u.ativo ? 'Desativar' : 'Ativar'}
                  </button>
                )}
                <button
                  onClick={() => openEdit(u)}
                  className="rounded-lg bg-white/10 px-3 py-1.5 text-[10px] font-semibold hover:bg-white/20"
                >
                  ✏️ Editar
                </button>
                {u.login !== 'admin' && (
                  <button
                    onClick={() => setDeleteModal({ open: true, user: u })}
                    className="rounded-lg bg-red-500/20 px-3 py-1.5 text-[10px] font-semibold text-red-300 hover:bg-red-500/30"
                  >
                    🗑️ Excluir
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Modal open={open} title={editingUser ? 'Editar usuário' : 'Novo usuário'} onClose={() => { setOpen(false); setEditingUser(null) }}>
        <form className="space-y-3" onSubmit={editingUser ? onUpdate : onCreate}>
          <div>
            <div className="text-xs text-white/70">Nome completo *</div>
            <input
              value={form.nome}
              onChange={(e) => setForm(f => ({ ...f, nome: e.target.value }))}
              className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm outline-none"
              placeholder="Ex: João Silva"
            />
          </div>
          
          <div>
            <div className="text-xs text-white/70">Login *</div>
            <input
              value={form.login}
              onChange={(e) => setForm(f => ({ ...f, login: e.target.value.toLowerCase().replace(/[^a-z0-9_@.-]/g, '') }))}
              className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm outline-none font-mono"
              placeholder="Ex: joao_silva"
              disabled={!!editingUser}
            />
            {editingUser && <div className="mt-1 text-[10px] text-white/50">Login não pode ser alterado</div>}
            {!editingUser && <div className="mt-1 text-[10px] text-white/50">Email será: {form.login || '...'}</div>}
          </div>
          
          <div>
            <div className="text-xs text-white/70">Senha (máx. 6 dígitos) {editingUser ? '(deixe vazio para manter)' : '*'}</div>
            <div className="mt-1 relative">
              <input
                type={showPin ? 'text' : 'password'}
                inputMode="numeric"
                pattern="\d{4,6}"
                maxLength={6}
                value={form.senha || ''}
                onChange={(e) => setForm(f => ({ ...f, senha: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                className="w-full rounded-xl bg-white/10 px-3 py-3 pr-10 text-sm outline-none font-mono"
                placeholder={editingUser ? 'Novo PIN (opcional)' : 'Ex: 1234'}
              />
              <button
                type="button"
                onClick={() => setShowPin(!showPin)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-white/60 hover:text-white"
              >
                {showPin ? '�' : '👁️'}
              </button>
            </div>
          </div>
          
          <div>
            <div className="text-xs text-white/70">Tipo de usuário *</div>
            <select
              value={form.perfil}
              onChange={(e) => setForm(f => ({ ...f, perfil: e.target.value }))}
              className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm"
            >
              {PERFIS_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <div className="mt-1 text-[10px] text-white/50">
              {form.perfil === 'admin' && 'Acesso total ao sistema'}
              {form.perfil === 'comprador' && 'Pode criar e gerenciar pedidos de compra'}
              {form.perfil === 'almoxarife' && 'Pode movimentar estoque e ver relatórios'}
            </div>
          </div>
          
          <button
            disabled={busy}
            className="w-full rounded-xl bg-accent-600 px-3 py-3 text-sm font-semibold text-white disabled:opacity-60"
          >
            {busy ? 'Salvando…' : editingUser ? 'Atualizar usuário' : 'Criar usuário'}
          </button>
        </form>
      </Modal>

      {/* Modal de Confirmação */}
      <Modal 
        open={deleteModal.open} 
        title="Confirmar exclusão"
        onClose={() => setDeleteModal({ open: false, user: null })}
      >
        <div className="text-center">
          <div className="text-sm mb-2">
            Tem certeza que deseja EXCLUIR permanentemente o usuário:
          </div>
          <div className="text-lg font-semibold text-red-300">
            {deleteModal.user?.nome}
          </div>
          <div className="text-xs text-white/70 mt-1 font-mono">
            {deleteModal.user?.login}
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            onClick={() => setDeleteModal({ open: false, user: null })}
            className="rounded-xl bg-white/10 px-3 py-2 text-sm font-semibold"
          >
            Cancelar
          </button>
          <button
            onClick={confirmDelete}
            className="rounded-xl bg-red-500 px-3 py-2 text-sm font-semibold text-white"
          >
            🗑️ Excluir
          </button>
        </div>
      </Modal>
    </div>
  )
}
